import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, TrendingUp, Calendar, DollarSign, ChevronDown,
  BarChart3, ChevronLeft, ChevronRight, Receipt, Plus, Trash2,
  Filter, Download, Search, Target, Award, ArrowUpRight, Clock, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth, api } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import { toast } from 'sonner';
const StaffDashboardPage = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [allStaffRevenue, setAllStaffRevenue] = useState([]);
  // Controlo de loading separado: inicial vs. navegação de semana
  const [initialLoading, setInitialLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('clients');
  const [rankingPeriod, setRankingPeriod] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [expenseDesc, setExpenseDesc] = useState('');
  const [expenseValue, setExpenseValue] = useState('');
  const [expenseDate, setExpenseDate] = useState(new Date().toISOString().split('T')[0]);
  const [expenseMonthFilter, setExpenseMonthFilter] = useState(new Date().getMonth());
  const [annualViewMode, setAnnualViewMode] = useState('net');
  const [searchTerm, setSearchTerm] = useState('');
  const [weeklyGoal] = useState(300);
  // Working Hours state
  const [customHoursEnabled, setCustomHoursEnabled] = useState(false);
  const [workingHours, setWorkingHours] = useState({
    mon: { enabled: true, start: '08:00', end: '20:00' },
    tue: { enabled: true, start: '08:00', end: '20:00' },
    wed: { enabled: true, start: '08:00', end: '20:00' },
    thu: { enabled: true, start: '08:00', end: '20:00' },
    fri: { enabled: true, start: '08:00', end: '20:00' },
    sat: { enabled: false, start: '08:00', end: '20:00' },
    sun: { enabled: false, start: '08:00', end: '20:00' },
  });
  const [hoursSaving, setHoursSaving] = useState(false);
  const [myStaffId, setMyStaffId] = useState(null);
  // Lead Time state
  const [leadTimeEnabled, setLeadTimeEnabled] = useState(false);
  const [leadTimeWeekday, setLeadTimeWeekday] = useState(0);
  const [leadTimeWeekend, setLeadTimeWeekend] = useState(0);
  const isAdmin = user?.role === 'admin';
  const isSuperAdmin = (user?.email || '').trim().toLowerCase() === 'irineu.novais.junior\@gmail.com';
  const isStaff = user?.role === 'staff' || user?.role === 'admin';
  // Data visualizada baseada no weekOffset
  const viewedDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + weekOffset * 7);
    return d;
  }, [weekOffset]);
  const viewedMonth = viewedDate.getMonth();
  const viewedYear = viewedDate.getFullYear();
  // ─────────────────────────────────────────────
  // FETCH INICIAL — clientes + despesas + admin
  // Corre apenas uma vez ao montar o componente
  // ─────────────────────────────────────────────
  const fetchInitialData = useCallback(async () => {
    if (!user || !isStaff) return;
    setInitialLoading(true);
    // Cada chamada é independente — uma falha não bloqueia as outras
    const results = await Promise.allSettled([
      api.get('/staff/clients/mine'),
      api.get(`/staff/expenses?year=${viewedYear}`),
      isSuperAdmin ? api.get('/admin/all-staff-revenue') : Promise.resolve(null),
      api.get(`/staff/stats/revenue?week_offset=0`),
      api.get('/staff'),
    ]);
    const [clientsResult, expensesResult, adminResult, statsResult, staffResult] = results;
    if (clientsResult.status === 'fulfilled') {
      setClients((clientsResult.value?.data || []).sort((a, b) => b.total_spent - a.total_spent));
    } else {
      console.warn('clients/mine failed:', clientsResult.reason?.response?.data || clientsResult.reason);
    }
    if (expensesResult.status === 'fulfilled') {
      setExpenses(expensesResult.value?.data || []);
    } else {
      console.warn('expenses failed:', expensesResult.reason?.response?.data || expensesResult.reason);
    }
    if (isSuperAdmin && adminResult.status === 'fulfilled' && adminResult.value) {
      setAllStaffRevenue(adminResult.value?.data || []);
    }
    if (statsResult.status === 'fulfilled') {
      setStats(statsResult.value?.data || null);
    } else {
      console.warn('stats failed:', statsResult.reason?.response?.data || statsResult.reason);
    }
    // Load working hours from own staff profile
    if (staffResult.status === 'fulfilled') {
      const staffList = staffResult.value?.data || [];
      const me = staffList.find(s => s.email === user?.email);
      if (me) {
        setMyStaffId(me.staff_id);
        if (me.custom_hours_enabled) setCustomHoursEnabled(true);
        if (me.working_hours && Object.keys(me.working_hours).length > 0) {
          setWorkingHours(prev => ({ ...prev, ...me.working_hours }));
        }
        if (me.lead_time_enabled) setLeadTimeEnabled(true);
        if (me.lead_time_weekday) setLeadTimeWeekday(me.lead_time_weekday);
        if (me.lead_time_weekend) setLeadTimeWeekend(me.lead_time_weekend);
      }
    }
    setInitialLoading(false);
  }, [user, isStaff, isAdmin, isSuperAdmin, viewedYear]);
  // ─────────────────────────────────────────────
  // FETCH DE STATS — corre ao mudar weekOffset
  // NÃO faz loading completo, só actualiza stats
  // ─────────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    if (!user || !isStaff) return;
    setStatsLoading(true);
    try {
      const statsRes = await api.get(`/staff/stats/revenue?week_offset=${weekOffset}`);
      setStats(statsRes.data || null);
    } catch (err) {
      console.warn('fetchStats failed:', err?.response?.data || err);
      // falha silenciosa — mantém stats anteriores, sem toast
    } finally {
      setStatsLoading(false);
    }
  }, [user, isStaff, weekOffset]);
  // ─────────────────────────────────────────────
  // FETCH DE DESPESAS — quando o ano muda ao navegar
  // ─────────────────────────────────────────────
  const fetchExpenses = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get(`/staff/expenses?year=${viewedYear}`);
      setExpenses(res.data || []);
    } catch (err) {
      console.error('Error fetching expenses:', err);
    }
  }, [user, viewedYear]);
  useEffect(() => {
    if (!user) return;
    if (!isStaff) { navigate('/dashboard'); return; }
    fetchInitialData();
    api.get('/staff/clients/ranking-period').then(r => setRankingPeriod(r.data?.period_start)).catch(() => {});
  }, [user, isStaff]);
  // Ao mudar weekOffset: busca só as stats (sem loading completo)
  // Se o ano mudou, busca também as despesas desse ano
  const prevYearRef = React.useRef(viewedYear);
  useEffect(() => {
    if (!user || !isStaff) return;
    fetchStats();
    if (prevYearRef.current !== viewedYear) {
      prevYearRef.current = viewedYear;
      fetchExpenses();
    }
  }, [weekOffset]);
  // ─────────────────────────────────────────────
  // ADICIONAR DESPESA — guarda no backend e
  // actualiza o estado local sem re-fetch completo
  // ─────────────────────────────────────────────
  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!expenseDesc || !expenseValue || !expenseDate) return;
    try {
      const response = await api.post('/staff/expenses', {
        description: expenseDesc,
        amount: parseFloat(expenseValue),
        date: expenseDate,
      });
      // Adiciona localmente (sem re-fetch para não perder contexto)
      setExpenses(prev => [response.data, ...prev]);
      setExpenseDesc('');
      setExpenseValue('');
      setExpenseDate(new Date().toISOString().split('T')[0]);
      setShowExpenseForm(false);
      toast.success(language === 'pt' ? 'Despesa adicionada e guardada!' : 'Expense saved!');
    } catch (error) {
      console.error('Error adding expense:', error);
      toast.error(language === 'pt' ? 'Erro ao guardar despesa' : 'Error saving expense');
    }
  };
  // ─────────────────────────────────────────────
  // APAGAR DESPESA
  // ─────────────────────────────────────────────
  const deleteExpense = async (id) => {
    if (!window.confirm(language === 'pt' ? 'Excluir esta despesa?' : 'Delete this expense?')) return;
    try {
      await api.delete(`/staff/expenses/${id}`);
      setExpenses(prev => prev.filter(e => e.expense_id !== id && e.id !== id));
      toast.success(language === 'pt' ? 'Despesa excluída' : 'Expense deleted');
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast.error(language === 'pt' ? 'Erro ao excluir despesa' : 'Error deleting expense');
    }
  };
  // ─────────────────────────────────────────────
  // GUARDAR HORÁRIO DE TRABALHO
  // ─────────────────────────────────────────────
  const handleSaveWorkingHours = async () => {
    setHoursSaving(true);
    try {
      const payload = {
        custom_hours_enabled: customHoursEnabled,
        working_hours: workingHours,
        lead_time_enabled: leadTimeEnabled,
        lead_time_weekday: parseInt(leadTimeWeekday) || 0,
        lead_time_weekend: parseInt(leadTimeWeekend) || 0,
      };
      // Se tem staff_id próprio, usa self-update; senão (admin sem staff), usa endpoint admin
      if (myStaffId) {
        await api.put(`/staff/${myStaffId}/working-hours`, payload);
      } else {
        await api.put('/staff/working-hours', payload);
      }
      toast.success(language === 'pt' ? 'Horário atualizado com sucesso' : 'Working hours updated');
    } catch (error) {
      console.error('Error saving working hours:', error);
      toast.error(language === 'pt' ? 'Erro ao guardar horário' : 'Error saving working hours');
    } finally {
      setHoursSaving(false);
    }
  };
  const dayLabels = {
    mon: language === 'pt' ? 'Segunda' : 'Monday',
    tue: language === 'pt' ? 'Terça' : 'Tuesday',
    wed: language === 'pt' ? 'Quarta' : 'Wednesday',
    thu: language === 'pt' ? 'Quinta' : 'Thursday',
    fri: language === 'pt' ? 'Sexta' : 'Friday',
    sat: language === 'pt' ? 'Sábado' : 'Saturday',
    sun: language === 'pt' ? 'Domingo' : 'Sunday',
  };
  const exportToCSV = (data, fileName) => {
    if (!data || data.length === 0) return;
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(obj => Object.values(obj).map(v => `"${v}"`).join(','));
    const csvContent = 'data:text/csv;charset=utf-8,' + headers + '\n' + rows.join('\n');
    const link = document.createElement('a');
    link.setAttribute('href', encodeURI(csvContent));
    link.setAttribute('download', `${fileName}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const exportRankingExcel = async () => {
    try {
      const res = await api.get('/staff/clients/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `ranking_clientes_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(language === 'pt' ? 'Erro ao exportar o ranking.' : 'Failed to export ranking.');
    }
  };
  const resetRanking = async () => {
    if (!window.confirm(language === 'pt'
      ? 'Repor o ranking a zero? Começa uma nova contagem a partir de agora.'
      : 'Reset ranking to zero? A new count starts from now.')) return;
    try {
      await api.post('/staff/clients/reset-ranking');
      toast.success(language === 'pt' ? 'Ranking reposto!' : 'Ranking reset!');
      try {
        const p = await api.get('/staff/clients/ranking-period');
        setRankingPeriod(p.data?.period_start);
      } catch { /* noop */ }
      const r = await api.get('/staff/clients/mine');
      setClients((r.data || []).sort((a, b) => b.total_spent - a.total_spent));
    } catch {
      toast.error(language === 'pt' ? 'Erro ao repor o ranking.' : 'Failed to reset ranking.');
    }
  };
  // ─────────────────────────────────────────────
  // CÁLCULOS DE DESPESAS
  // ─────────────────────────────────────────────
  const getMonthExpenses = useCallback((monthIndex, year) => {
    return expenses
      .filter(exp => {
        // O backend guarda date como "2026-04-04" (sem hora)
        // Ao fazer new Date("2026-04-04") o JS interpreta como UTC meia-noite
        // e em fusos + pode virar dia anterior → usar split para ser seguro
        const parts = (exp.date || '').split('T')[0].split('-');
        if (parts.length < 3) return false;
        const expYear = parseInt(parts[0]);
        const expMonth = parseInt(parts[1]) - 1; // 0-indexed
        return expMonth === monthIndex && expYear === year;
      })
      .reduce((sum, exp) => sum + (Number(exp.amount) || 0), 0);
  }, [expenses]);
  // Despesas do mês visualizado (para o card de topo)
  const viewedMonthExpensesTotal = useMemo(
    () => getMonthExpenses(viewedMonth, viewedYear),
    [getMonthExpenses, viewedMonth, viewedYear]
  );
  // Receita líquida do mês
  const netRevenue = useMemo(
    () => (Number(stats?.monthly?.revenue) || 0) - viewedMonthExpensesTotal,
    [stats, viewedMonthExpensesTotal]
  );
  // Despesas filtradas para a aba (por mês seleccionado no filtro)
  const filteredExpensesForTab = useMemo(() => {
    return expenses.filter(exp => {
      const parts = (exp.date || '').split('T')[0].split('-');
      if (parts.length < 3) return false;
      const expYear = parseInt(parts[0]);
      const expMonth = parseInt(parts[1]) - 1;
      return expMonth === expenseMonthFilter && expYear === viewedYear;
    });
  }, [expenses, expenseMonthFilter, viewedYear]);
  const totalExpensesInTab = useMemo(
    () => filteredExpensesForTab.reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0),
    [filteredExpensesForTab]
  );
  // Clientes filtrados por pesquisa
  const filteredClients = useMemo(() => {
    return clients.filter(c =>
      c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.phone?.includes(searchTerm)
    );
  }, [clients, searchTerm]);
  // ─────────────────────────────────────────────
  // DADOS DO GRÁFICO ANUAL
  // ─────────────────────────────────────────────
  const chartData = useMemo(() => {
    return Array.from({ length: 12 }).map((_, index) => {
      const monthData = stats?.yearly_breakdown?.find(m => m.month_index === index);
      let grossValue = Number(monthData?.revenue || 0);
      // Se não veio do breakdown mas é o mês actual, usa o mensal
      if (!monthData && index === viewedMonth) {
        grossValue = Number(stats?.monthly?.revenue || 0);
      }
      const monthExp = getMonthExpenses(index, viewedYear);
      const netValue = grossValue - monthExp;
      return {
        index,
        gross: grossValue,
        net: netValue,
        exp: monthExp,
        finalValue: annualViewMode === 'gross' ? grossValue : netValue,
        isViewedMonth: index === viewedMonth,
      };
    });
  }, [stats, getMonthExpenses, viewedMonth, viewedYear, annualViewMode]);
  const maxChartVal = Math.max(...chartData.map(d => Math.abs(d.finalValue)), 100);
  const yearlyGross = useMemo(
    () => Number(stats?.yearly?.revenue || 0) || chartData.reduce((sum, d) => sum + d.gross, 0),
    [stats, chartData]
  );
  const yearlyExpenses = useMemo(
    () => Array.from({ length: 12 }).reduce((sum, _, i) => sum + getMonthExpenses(i, viewedYear), 0),
    [getMonthExpenses, viewedYear]
  );
  const yearlyNet = yearlyGross - yearlyExpenses;
  // ─────────────────────────────────────────────
  // UTILITÁRIOS DE FORMATAÇÃO
  // ─────────────────────────────────────────────
  const formatCurrency = (value) => `€${(Number(value) || 0).toFixed(2)}`;
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    // Usar split para evitar bug de timezone no parse de datas sem hora
    const parts = dateStr.split('T')[0].split('-');
    if (parts.length < 3) return dateStr;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };
  const renderRankMedal = (index) => {
    if (index === 0) return <span className="text-2xl animate-bounce inline-block">🥇</span>;
    if (index === 1) return <span className="text-2xl">🥈</span>;
    if (index === 2) return <span className="text-2xl">🥉</span>;
    return <span className="text-sm font-medium text-[#A8A29A]">#{index + 1}</span>;
  };
  // ─────────────────────────────────────────────
  // LOADING INICIAL (página inteira)
  // ─────────────────────────────────────────────
  if (initialLoading) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] pt-24 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#BFA05F] border-t-transparent"></div>
          <p className="text-[#BFA05F] font-medium animate-pulse">Sincronizando dados...</p>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-[#F9F8F6] pt-24 pb-16" data-testid="staff-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="px-2 py-0.5 bg-[#BFA05F]/10 text-[#BFA05F] text-[10px] font-bold rounded-full uppercase tracking-wider">
                {user?.role} Mode
              </span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-medium text-[#1A1815]">
              {language === 'pt' ? 'Faturação e Horários' : 'Revenue & Schedule'}
            </h1>
            <p className="text-[#635E54] mt-2 flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              {language === 'pt' ? 'Controle de performance e financeiro em tempo real' : 'Real-time performance and financial control'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={exportRankingExcel} data-testid="export-ranking-btn"
              className="rounded-xl border-[#E6E2DA] text-[#635E54] hover:bg-white">
              <Download className="w-4 h-4 mr-2" />
              {language === 'pt' ? 'Exportar Excel' : 'Export Excel'}
            </Button>
            {isAdmin && (
              <Button variant="outline" onClick={resetRanking} data-testid="reset-ranking-btn"
                className="rounded-xl border-red-200 text-red-500 hover:bg-red-50">
                <RefreshCw className="w-4 h-4 mr-2" />
                {language === 'pt' ? 'Repor Ranking' : 'Reset Ranking'}
              </Button>
            )}
          </div>
        </div>
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {/* Hoje */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-[#E6E2DA] hover:shadow-md transition-shadow group">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <span className="text-sm text-[#635E54]">{language === 'pt' ? 'Hoje' : 'Today'}</span>
              </div>
              <p className="text-2xl font-bold text-[#1A1815]">{formatCurrency(stats.daily?.revenue)}</p>
              <p className="text-xs text-[#A8A29A]">{stats.daily?.bookings || 0} {language === 'pt' ? 'serviços' : 'services'}</p>
            </div>
            {/* Semana */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-[#BFA05F]/20 ring-1 ring-[#BFA05F]/30 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-2 opacity-10">
                <Target className="w-12 h-12 text-[#BFA05F]" />
              </div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-[#BFA05F]/10 rounded-xl flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-[#BFA05F]" />
                </div>
                <span className="text-sm font-semibold text-[#BFA05F]">
                  {statsLoading ? '...' : (language === 'pt' ? 'Faturação Semana' : 'Week Gross')}
                </span>
              </div>
              <p className="text-2xl font-bold text-[#1A1815]">
                {statsLoading ? <span className="animate-pulse text-[#BFA05F]">—</span> : formatCurrency(stats.weekly?.revenue)}
              </p>
              <div className="flex items-center gap-1 mt-1">
                <ArrowUpRight className="w-3 h-3 text-green-500" />
                <p className="text-xs text-[#A8A29A]">Meta: {formatCurrency(weeklyGoal)}</p>
              </div>
            </div>
            {/* Líquido Mês */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-[#E6E2DA]">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <span className="text-sm text-[#635E54]">{language === 'pt' ? 'Líquido Mês' : 'Net Month'}</span>
              </div>
              <p className={`text-2xl font-bold ${netRevenue < 0 ? 'text-red-600' : 'text-[#1A1815]'}`}>
                {statsLoading ? <span className="animate-pulse text-[#BFA05F]">—</span> : formatCurrency(netRevenue)}
              </p>
              <div className="w-full bg-gray-100 h-1.5 rounded-full mt-2 overflow-hidden">
                <div
                  className="bg-[#BFA05F] h-full transition-all duration-1000"
                  style={{ width: `${Math.max(0, Math.min(100, (netRevenue / (Number(stats?.monthly?.revenue) || 1)) * 100))}%` }}
                />
              </div>
            </div>
            {/* Gastos Mês */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-[#E6E2DA]">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                  <Receipt className="w-5 h-5 text-red-600" />
                </div>
                <span className="text-sm text-[#635E54]">{language === 'pt' ? 'Gastos (Mês)' : 'Expenses (Month)'}</span>
              </div>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(viewedMonthExpensesTotal)}</p>
              <p className="text-xs text-[#A8A29A]">
                {new Date(viewedYear, viewedMonth).toLocaleString(language === 'pt' ? 'pt-PT' : 'en-US', { month: 'long', year: 'numeric' })}
              </p>
            </div>
          </div>
        )}
        {/* Weekly Chart */}
        {stats?.daily_breakdown && (
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-[#E6E2DA] mb-8">
            <div className="flex flex-col sm:flex-row items-center justify-between mb-8 gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#F9F8F6] rounded-lg">
                  <BarChart3 className="w-5 h-5 text-[#BFA05F]" />
                </div>
                <h3 className="font-heading text-lg font-medium text-[#1A1815]">
                  {language === 'pt' ? 'Frequência de Faturação' : 'Revenue Frequency'}
                </h3>
              </div>
              {/* Navegação de semana — SEM loading completo */}
              <div className="flex items-center gap-2 bg-[#F9F8F6] p-1.5 rounded-2xl border border-[#E6E2DA]">
                <Button variant="ghost" size="sm"
                  onClick={() => setWeekOffset(w => w - 1)}
                  disabled={statsLoading}
                  className="rounded-xl h-8 w-8 p-0 hover:bg-white disabled:opacity-40">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-xs font-bold text-[#1A1815] min-w-[120px] text-center uppercase tracking-tighter flex items-center justify-center gap-1">
                  {statsLoading && <span className="w-2 h-2 bg-[#BFA05F] rounded-full animate-pulse inline-block" />}
                  {weekOffset === 0
                    ? (language === 'pt' ? 'Esta semana' : 'This week')
                    : weekOffset < 0
                      ? `${Math.abs(weekOffset)} ${language === 'pt' ? 'sem. atrás' : 'weeks ago'}`
                      : `${weekOffset} ${language === 'pt' ? 'sem. à frente' : 'weeks ahead'}`}
                </span>
                <Button variant="ghost" size="sm"
                  onClick={() => setWeekOffset(w => w + 1)}
                  disabled={statsLoading}
                  className="rounded-xl h-8 w-8 p-0 hover:bg-white disabled:opacity-40">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
            {/* Barras diárias */}
            <div className={`flex items-end gap-3 h-48 px-2 transition-opacity duration-300 ${statsLoading ? 'opacity-40' : 'opacity-100'}`}>
              {stats.daily_breakdown.map((day, index) => {
                const maxRevenue = Math.max(...stats.daily_breakdown.map(d => d.revenue), 1);
                const height = (day.revenue / maxRevenue) * 100;
                return (
                  <div key={index} className="flex-1 flex flex-col items-center gap-3 group">
                    <div className="w-full bg-[#F2F0EB] rounded-xl relative h-32 overflow-hidden">
                      <div
                        className="absolute bottom-0 w-full bg-gradient-to-t from-[#BFA05F] to-[#D4B96A] rounded-xl transition-all duration-700 ease-out"
                        style={{ height: `${height}%` }}
                      >
                        <div className="opacity-0 group-hover:opacity-100 absolute -top-8 left-1/2 -translate-x-1/2 bg-[#1A1815] text-white text-[10px] py-1 px-2 rounded-md transition-opacity whitespace-nowrap z-10">
                          {formatCurrency(day.revenue)}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-center">
                      <span className="text-[10px] font-bold text-[#1A1815] uppercase">{day.day_name?.substring(0, 3)}</span>
                      <span className="text-[9px] text-[#A8A29A] font-medium">{formatCurrency(day.revenue)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {/* Tabs Navigation */}
        <div className="flex gap-8 mb-6 border-b border-[#E6E2DA] overflow-x-auto no-scrollbar">
          {[
            { id: 'clients', label: language === 'pt' ? 'Ranking Clientes' : 'Client Ranking', icon: <Award className="w-4 h-4" /> },
            { id: 'expenses', label: language === 'pt' ? 'Despesas' : 'Expenses', icon: <Receipt className="w-4 h-4" /> },
            { id: 'annual', label: language === 'pt' ? 'Lucro Anual' : 'Annual Profit', icon: <TrendingUp className="w-4 h-4" /> },
            { id: 'hours', label: language === 'pt' ? 'Horário' : 'Schedule', icon: <Clock className="w-4 h-4" /> },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`pb-4 text-sm font-medium transition-all relative flex items-center gap-2 whitespace-nowrap ${activeTab === tab.id ? 'text-[#BFA05F]' : 'text-[#635E54] hover:text-[#1A1815]'}`}>
              {tab.icon}
              {tab.label}
              {activeTab === tab.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#BFA05F]" />}
            </button>
          ))}
        </div>
        {/* ── Tab: Ranking Clientes ── */}
        {activeTab === 'clients' && (
          <div className="space-y-4 animate-in fade-in duration-500">
            <div className="flex items-center gap-2 text-xs text-[#A8A29A]" data-testid="ranking-period-label">
              <RefreshCw className="w-3.5 h-3.5" />
              {rankingPeriod
                ? (language === 'pt'
                    ? `Ranking desde ${new Date(rankingPeriod).toLocaleDateString('pt-PT', { timeZone: 'Europe/Lisbon' })} · apenas marcações deste mês`
                    : `Ranking since ${new Date(rankingPeriod).toLocaleDateString('en-GB', { timeZone: 'Europe/Lisbon' })} · current-month bookings only`)
                : (language === 'pt' ? 'Ranking do mês atual · meses seguintes excluídos' : 'Current month only · future months excluded')}
            </div>
            <div className="relative group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A8A29A] group-focus-within:text-[#BFA05F] transition-colors" />
              <input type="text"
                placeholder={language === 'pt' ? 'Pesquisar por nome ou telefone...' : 'Search by name or phone...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-white rounded-2xl border border-[#E6E2DA] focus:ring-2 focus:ring-[#BFA05F]/20 outline-none transition-all"
              />
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-[#E6E2DA] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#F9F8F6]">
                    <tr>
                      <th className="text-center px-6 py-4 text-sm font-medium text-[#635E54]">Pos</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-[#635E54]">{language === 'pt' ? 'Cliente' : 'Client'}</th>
                      <th className="text-center px-6 py-4 text-sm font-medium text-[#635E54]">{language === 'pt' ? 'Visitas' : 'Visits'}</th>
                      <th className="text-right px-6 py-4 text-sm font-medium text-[#635E54]">{language === 'pt' ? 'Total Gasto' : 'Total Spent'}</th>
                      <th className="text-right px-6 py-4 text-sm font-medium text-[#635E54]">{language === 'pt' ? 'Última Visita' : 'Last Visit'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E6E2DA]">
                    {filteredClients.map((client, index) => (
                      <tr key={client.user_id}
                        className={`hover:bg-[#F9F8F6]/50 transition-colors ${index < 3 && !searchTerm ? 'bg-[#BFA05F]/5' : ''}`}>
                        <td className="px-6 py-4 text-center">{renderRankMedal(index)}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-xs
                              ${index === 0 ? 'bg-yellow-100 text-yellow-700 ring-2 ring-yellow-400' :
                                index === 1 ? 'bg-slate-100 text-slate-600 ring-2 ring-slate-300' :
                                  index === 2 ? 'bg-orange-100 text-orange-700 ring-2 ring-orange-300' :
                                    'bg-[#BFA05F]/10 text-[#BFA05F]'}`}>
                              {client.name?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium text-[#1A1815]">{client.name}</p>
                              <p className="text-xs text-[#A8A29A]">{client.phone || '-'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-[#BFA05F]/10 text-[#BFA05F] text-xs font-bold">
                            {client.booking_count}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right font-bold text-[#1A1815]">{formatCurrency(client.total_spent)}</td>
                        <td className="px-6 py-4 text-right text-xs text-[#635E54]">{formatDate(client.last_visit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredClients.length === 0 && (
                  <div className="text-center py-16 flex flex-col items-center gap-2">
                    <Users className="w-12 h-12 text-[#E6E2DA]" />
                    <p className="text-[#A8A29A]">{language === 'pt' ? 'Nenhum cliente encontrado.' : 'No clients found.'}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {/* ── Tab: Despesas ── */}
        {activeTab === 'expenses' && (
          <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-500">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-white p-4 rounded-2xl border border-[#E6E2DA]">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-50 rounded-lg">
                  <Filter className="w-4 h-4 text-red-500" />
                </div>
                <select
                  value={expenseMonthFilter}
                  onChange={(e) => setExpenseMonthFilter(parseInt(e.target.value))}
                  className="bg-transparent text-sm font-bold text-[#1A1815] outline-none cursor-pointer"
                >
                  {Array.from({ length: 12 }).map((_, i) => (
                    <option key={i} value={i}>
                      {new Date(0, i).toLocaleString(language === 'pt' ? 'pt-PT' : 'en-US', { month: 'long' })}
                      {' '}{viewedYear}
                    </option>
                  ))}
                </select>
              </div>
              <Button onClick={() => setShowExpenseForm(!showExpenseForm)}
                className="bg-[#1A1815] text-white rounded-xl px-6 hover:bg-[#333]">
                <Plus className="w-4 h-4 mr-2" />
                {language === 'pt' ? 'Lançar Despesa' : 'Record Expense'}
              </Button>
            </div>
            {showExpenseForm && (
              <div className="bg-white p-6 rounded-2xl border-2 border-[#BFA05F]/30">
                <form onSubmit={handleAddExpense} className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div>
                    <label className="block text-[10px] font-bold text-[#A8A29A] uppercase mb-2">
                      {language === 'pt' ? 'Data do Gasto' : 'Date of Expense'}
                    </label>
                    <input type="date" value={expenseDate}
                      onChange={(e) => setExpenseDate(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-xl border border-[#E6E2DA] focus:ring-2 focus:ring-[#BFA05F]/50 outline-none text-sm" required />
                  </div>
                  <div className="md:col-span-1">
                    <label className="block text-[10px] font-bold text-[#A8A29A] uppercase mb-2">
                      {language === 'pt' ? 'Descrição / Categoria' : 'Description / Category'}
                    </label>
                    <input type="text" value={expenseDesc}
                      onChange={(e) => setExpenseDesc(e.target.value)}
                      placeholder="Material, Renda..."
                      className="w-full px-4 py-2.5 rounded-xl border border-[#E6E2DA] focus:ring-2 focus:ring-[#BFA05F]/50 outline-none text-sm" required />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-[#A8A29A] uppercase mb-2">
                      {language === 'pt' ? 'Valor (€)' : 'Amount (€)'}
                    </label>
                    <input type="number" step="0.01" value={expenseValue}
                      onChange={(e) => setExpenseValue(e.target.value)}
                      placeholder="0.00"
                      className="w-full px-4 py-2.5 rounded-xl border border-[#E6E2DA] focus:ring-2 focus:ring-[#BFA05F]/50 outline-none text-sm font-bold" required />
                  </div>
                  <div className="flex items-end">
                    <Button type="submit" className="w-full bg-[#BFA05F] text-white rounded-xl h-[42px] font-bold shadow-lg shadow-[#BFA05F]/20">
                      {language === 'pt' ? 'Confirmar' : 'Confirm'}
                    </Button>
                  </div>
                </form>
              </div>
            )}
            <div className="bg-white rounded-2xl shadow-sm border border-[#E6E2DA] overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#F9F8F6]">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#635E54]">{language === 'pt' ? 'Data' : 'Date'}</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#635E54]">{language === 'pt' ? 'Descrição' : 'Description'}</th>
                    <th className="text-right px-6 py-4 text-sm font-medium text-[#635E54]">{language === 'pt' ? 'Valor' : 'Amount'}</th>
                    <th className="text-center px-6 py-4 text-sm font-medium text-[#635E54]">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E6E2DA]">
                  {filteredExpensesForTab.length > 0 ? (
                    filteredExpensesForTab.map((exp) => (
                      <tr key={exp.expense_id || exp.id} className="hover:bg-red-50/20 transition-colors group">
                        <td className="px-6 py-4 text-sm text-[#635E54]">{formatDate(exp.date)}</td>
                        <td className="px-6 py-4 text-sm font-medium text-[#1A1815]">{exp.description}</td>
                        <td className="px-6 py-4 text-right text-sm font-bold text-red-600">-{formatCurrency(exp.amount)}</td>
                        <td className="px-6 py-4 text-center">
                          <button
                            onClick={() => deleteExpense(exp.expense_id || exp.id)}
                            className="p-2 text-[#A8A29A] hover:text-red-600 hover:bg-red-50 rounded-lg transition-all opacity-0 group-hover:opacity-100">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="px-6 py-20 text-center text-[#A8A29A]">
                        <div className="flex flex-col items-center gap-2">
                          <Receipt className="w-10 h-10 opacity-20" />
                          <p>{language === 'pt' ? 'Nenhuma despesa registrada para este período.' : 'No expenses recorded for this period.'}</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {filteredExpensesForTab.length > 0 && (
                <div className="p-4 bg-[#F9F8F6] border-t border-[#E6E2DA] flex justify-between items-center">
                  <p className="text-xs text-[#A8A29A]">
                    {filteredExpensesForTab.length} {language === 'pt' ? 'registo(s)' : 'record(s)'}
                  </p>
                  <p className="text-sm font-bold text-[#1A1815]">
                    Total: <span className="text-red-600">-{formatCurrency(totalExpensesInTab)}</span>
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
        {/* ── Tab: Lucro Anual ── */}
        {activeTab === 'annual' && stats && (
          <div className="bg-white p-8 rounded-3xl shadow-sm border border-[#E6E2DA] animate-in fade-in zoom-in-95 duration-500">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-6">
              <div>
                <h3 className="font-heading text-2xl text-[#1A1815]">
                  {language === 'pt' ? 'Performance de Ciclo' : 'Cycle Performance'}
                </h3>
                <p className="text-sm text-[#635E54]">
                  {language === 'pt' ? 'Visão consolidada de crescimento anual' : 'Consolidated annual growth view'}
                  {' — '}{viewedYear}
                </p>
              </div>
              <div className="flex bg-[#F9F8F6] p-1.5 rounded-2xl border border-[#E6E2DA] shadow-inner">
                <button onClick={() => setAnnualViewMode('gross')}
                  className={`px-6 py-2 text-[10px] font-black rounded-xl transition-all ${annualViewMode === 'gross' ? 'bg-white shadow-md text-[#1A1815]' : 'text-[#A8A29A]'}`}>
                  {language === 'pt' ? 'BRUTO' : 'GROSS'}
                </button>
                <button onClick={() => setAnnualViewMode('net')}
                  className={`px-6 py-2 text-[10px] font-black rounded-xl transition-all ${annualViewMode === 'net' ? 'bg-[#BFA05F] shadow-md text-white' : 'text-[#A8A29A]'}`}>
                  {language === 'pt' ? 'LÍQUIDO' : 'NET'}
                </button>
              </div>
            </div>
            {/* Gráfico anual — mostra todos os meses, incluindo os com valor 0 */}
            <div className={`flex items-end gap-2 h-80 border-b border-[#E6E2DA] pb-4 mb-10 relative pt-14 px-2 transition-opacity duration-300 ${statsLoading ? 'opacity-40' : 'opacity-100'}`}>
              <div className="absolute inset-0 flex flex-col justify-between pointer-events-none opacity-5">
                {[...Array(5)].map((_, i) => <div key={i} className="w-full border-t border-[#1A1815]" />)}
              </div>
              {chartData.map((data) => {
                const isNegative = data.finalValue < 0;
                const barHeight = maxChartVal > 0
                  ? Math.min(100, (Math.abs(data.finalValue) / maxChartVal) * 100)
                  : 0;
                const hasData = data.gross > 0 || data.exp > 0;
                return (
                  <div key={data.index}
                    className="flex-1 flex flex-col items-center gap-2 group relative z-10"
                    title={`${new Date(viewedYear, data.index).toLocaleString(language === 'pt' ? 'pt-PT' : 'en-US', { month: 'long' })}: Bruto ${formatCurrency(data.gross)} | Despesas ${formatCurrency(data.exp)} | Líquido ${formatCurrency(data.net)}`}>
                    {/* Tag de valor — só mostra se tiver dados */}
                    <div className="mb-auto">
                      {hasData ? (
                        <div className={`text-[9px] font-bold px-2 py-1 rounded-lg whitespace-nowrap shadow-sm ${isNegative ? 'bg-red-600 text-white' : 'bg-[#1A1815] text-white'}`}>
                          {isNegative ? '-' : ''}{formatCurrency(Math.abs(data.finalValue))}
                        </div>
                      ) : (
                        <div className="text-[9px] text-[#E6E2DA] px-2 py-1">—</div>
                      )}
                    </div>
                    <div className="w-full bg-[#F2F0EB]/40 rounded-t-xl relative h-[220px] overflow-hidden group-hover:bg-[#F2F0EB] transition-all duration-300">
                      {barHeight > 0 && (
                        <div
                          className={`absolute bottom-0 w-full transition-all duration-1000 ease-out rounded-t-xl ${
                            isNegative
                              ? 'bg-gradient-to-t from-red-500 to-red-400'
                              : annualViewMode === 'gross'
                                ? 'bg-[#A8A29A]'
                                : 'bg-gradient-to-t from-[#BFA05F] to-[#D4B96A]'
                          } ${data.isViewedMonth ? 'ring-2 ring-inset ring-white/30' : ''}`}
                          style={{ height: `${barHeight}%` }}
                        />
                      )}
                      {data.isViewedMonth && (
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-[#BFA05F] rounded-full mt-1 animate-pulse shadow-[0_0_8px_#BFA05F]" />
                      )}
                    </div>
                    <span className={`text-[10px] font-black uppercase tracking-tighter transition-colors ${data.isViewedMonth ? 'text-[#BFA05F]' : 'text-[#A8A29A] group-hover:text-[#1A1815]'}`}>
                      {new Date(viewedYear, data.index).toLocaleString(language === 'pt' ? 'pt-PT' : 'en-US', { month: 'short' }).replace('.', '')}
                    </span>
                  </div>
                );
              })}
            </div>
            {/* Legenda bruto vs despesas vs líquido */}
            <div className="flex gap-4 justify-center mb-6 text-xs text-[#635E54]">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-sm bg-[#A8A29A] inline-block" /> {language === 'pt' ? 'Bruto' : 'Gross'}
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-sm bg-gradient-to-t from-[#BFA05F] to-[#D4B96A] inline-block" /> {language === 'pt' ? 'Líquido' : 'Net'}
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-sm bg-red-400 inline-block" /> {language === 'pt' ? 'Negativo' : 'Negative'}
              </span>
            </div>
            {/* Footer Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-6 rounded-3xl bg-[#F9F8F6] border border-[#E6E2DA] hover:border-[#A8A29A] transition-colors group">
                <p className="text-[10px] font-black text-[#A8A29A] uppercase mb-2 tracking-widest">
                  {language === 'pt' ? 'Total Bruto Anual' : 'Annual Gross'}
                </p>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-black text-[#1A1815]">{formatCurrency(yearlyGross)}</span>
                  <span className="text-xs text-green-600 font-bold">YTD</span>
                </div>
              </div>
              <div className="p-6 rounded-3xl bg-red-50 border border-red-100 transition-colors group">
                <p className="text-[10px] font-black text-red-400 uppercase mb-2 tracking-widest">
                  {language === 'pt' ? 'Total Despesas Anuais' : 'Annual Expenses'}
                </p>
                <span className="text-3xl font-black text-red-600">-{formatCurrency(yearlyExpenses)}</span>
              </div>
              <div className="p-6 rounded-3xl bg-[#BFA05F]/5 border border-[#BFA05F]/20 hover:bg-[#BFA05F]/10 transition-colors group relative overflow-hidden">
                <div className="absolute -right-4 -bottom-4 opacity-5 group-hover:scale-110 transition-transform duration-700">
                  <TrendingUp className="w-24 h-24" />
                </div>
                <p className="text-[10px] font-black text-[#BFA05F] uppercase mb-2 tracking-widest">
                  {language === 'pt' ? 'Líquido Estimado' : 'Estimated Net'}
                </p>
                <p className={`text-3xl font-black ${yearlyNet < 0 ? 'text-red-600' : 'text-[#1A1815]'}`}>
                  {formatCurrency(yearlyNet)}
                </p>
              </div>
            </div>
          </div>
        )}
        {/* ── Tab: Horário de Trabalho ── */}
        {activeTab === 'hours' && (
          <div className="space-y-6 animate-in fade-in duration-500">
            {/* Toggle */}
            <div className="p-6 rounded-3xl bg-white border border-[#E6E2DA]">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-[#1A1815]">
                    {language === 'pt' ? 'Horário Personalizado' : 'Custom Schedule'}
                  </h3>
                  <p className="text-sm text-[#635E54] mt-1">
                    {language === 'pt'
                      ? 'Quando desativado, aplica-se o horário padrão do salão (08:00 - 20:00)'
                      : 'When disabled, the standard salon hours apply (08:00 - 20:00)'}
                  </p>
                </div>
                <button
                  onClick={() => setCustomHoursEnabled(!customHoursEnabled)}
                  className={`relative w-14 h-7 rounded-full transition-colors duration-200 ${customHoursEnabled ? 'bg-[#BFA05F]' : 'bg-[#D4D0C8]'}`}
                  data-testid="custom-hours-toggle"
                >
                  <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform duration-200 ${customHoursEnabled ? 'translate-x-7' : 'translate-x-0.5'}`} />
                </button>
              </div>
            </div>
            {/* Weekly Grid */}
            {customHoursEnabled && (
              <div className="space-y-3">
                {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map(day => (
                  <div key={day}
                    className={`p-4 rounded-2xl border transition-all ${workingHours[day]?.enabled ? 'bg-white border-[#BFA05F]/30 shadow-sm' : 'bg-[#F9F8F6] border-[#E6E2DA] opacity-60'}`}>
                    <div className="flex items-center gap-4 flex-wrap">
                      {/* Day toggle */}
                      <label className="flex items-center gap-3 min-w-[140px] cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={workingHours[day]?.enabled || false}
                          onChange={() => setWorkingHours(prev => ({
                            ...prev,
                            [day]: { ...prev[day], enabled: !prev[day]?.enabled }
                          }))}
                          className="accent-[#BFA05F] w-5 h-5 rounded"
                        />
                        <span className={`text-sm font-semibold ${workingHours[day]?.enabled ? 'text-[#1A1815]' : 'text-[#A8A29A]'}`}>
                          {dayLabels[day]}
                        </span>
                      </label>
                      {/* Time inputs */}
                      {workingHours[day]?.enabled && (
                        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                          <input
                            type="time"
                            value={workingHours[day]?.start || '08:00'}
                            onChange={(e) => setWorkingHours(prev => ({
                              ...prev,
                              [day]: { ...prev[day], start: e.target.value }
                            }))}
                            className="px-3 py-2.5 rounded-xl border border-[#E6E2DA] bg-white text-sm text-[#1A1815] focus:border-[#BFA05F] focus:ring-1 focus:ring-[#BFA05F]/30 outline-none w-full max-w-[130px]"
                          />
                          <span className="text-[#A8A29A] text-sm font-medium">—</span>
                          <input
                            type="time"
                            value={workingHours[day]?.end || '19:00'}
                            onChange={(e) => setWorkingHours(prev => ({
                              ...prev,
                              [day]: { ...prev[day], end: e.target.value }
                            }))}
                            className="px-3 py-2.5 rounded-xl border border-[#E6E2DA] bg-white text-sm text-[#1A1815] focus:border-[#BFA05F] focus:ring-1 focus:ring-[#BFA05F]/30 outline-none w-full max-w-[130px]"
                          />
                        </div>
                      )}
                      {!workingHours[day]?.enabled && (
                        <span className="text-xs text-[#A8A29A] italic">
                          {language === 'pt' ? 'Folga' : 'Day off'}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                {/* Save button */}
                <Button
                  onClick={handleSaveWorkingHours}
                  disabled={hoursSaving}
                  className="w-full sm:w-auto bg-[#BFA05F] text-white hover:bg-[#9E8246] rounded-full px-8 h-12 text-sm font-semibold"
                  data-testid="save-working-hours-btn"
                >
                  <Clock className="w-4 h-4 mr-2" />
                  {hoursSaving
                    ? (language === 'pt' ? 'Guardando...' : 'Saving...')
                    : (language === 'pt' ? 'Guardar Horário' : 'Save Schedule')}
                </Button>
            {/* Lead Time Section */}
            <div className="p-6 rounded-3xl bg-white border border-[#E6E2DA]">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-base font-bold text-[#1A1815]">
                    {language === 'pt' ? 'Antecedência Mínima' : 'Minimum Lead Time'}
                  </h3>
                  <p className="text-sm text-[#635E54] mt-1">
                    {language === 'pt'
                      ? 'Quanto tempo antes a cliente precisa de marcar. Não se aplica a agendamentos internos.'
                      : 'How far in advance clients must book. Does not apply to internal bookings.'}
                  </p>
                </div>
                <button
                  onClick={() => setLeadTimeEnabled(!leadTimeEnabled)}
                  className={`relative w-14 h-7 rounded-full transition-colors duration-200 ${leadTimeEnabled ? 'bg-[#BFA05F]' : 'bg-[#D4D0C8]'}`}
                  data-testid="lead-time-toggle"
                >
                  <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform duration-200 ${leadTimeEnabled ? 'translate-x-7' : 'translate-x-0.5'}`} />
                </button>
              </div>
              {leadTimeEnabled && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <div className="p-4 rounded-2xl bg-[#F9F8F6] border border-[#E6E2DA]">
                    <label className="text-sm font-semibold text-[#1A1815] block mb-2">
                      {language === 'pt' ? 'Seg – Sex (minutos)' : 'Mon – Fri (minutes)'}
                    </label>
                    <select
                      value={leadTimeWeekday}
                      onChange={(e) => setLeadTimeWeekday(parseInt(e.target.value))}
                      className="w-full px-3 py-2.5 rounded-xl border border-[#E6E2DA] bg-white text-sm text-[#1A1815] focus:border-[#BFA05F] outline-none"
                    >
                      <option value={0}>0 min (imediato)</option>
                      <option value={15}>15 min</option>
                      <option value={30}>30 min</option>
                      <option value={60}>1 hora</option>
                      <option value={120}>2 horas</option>
                      <option value={180}>3 horas</option>
                      <option value={240}>4 horas</option>
                      <option value={360}>6 horas</option>
                      <option value={480}>8 horas</option>
                      <option value={720}>12 horas</option>
                      <option value={1440}>24 horas</option>
                    </select>
                  </div>
                  <div className="p-4 rounded-2xl bg-[#F9F8F6] border border-[#E6E2DA]">
                    <label className="text-sm font-semibold text-[#1A1815] block mb-2">
                      {language === 'pt' ? 'Sáb – Dom (minutos)' : 'Sat – Sun (minutes)'}
                    </label>
                    <select
                      value={leadTimeWeekend}
                      onChange={(e) => setLeadTimeWeekend(parseInt(e.target.value))}
                      className="w-full px-3 py-2.5 rounded-xl border border-[#E6E2DA] bg-white text-sm text-[#1A1815] focus:border-[#BFA05F] outline-none"
                    >
                      <option value={0}>0 min (imediato)</option>
                      <option value={15}>15 min</option>
                      <option value={30}>30 min</option>
                      <option value={60}>1 hora</option>
                      <option value={120}>2 horas</option>
                      <option value={180}>3 horas</option>
                      <option value={240}>4 horas</option>
                      <option value={360}>6 horas</option>
                      <option value={480}>8 horas</option>
                      <option value={720}>12 horas</option>
                      <option value={1440}>24 horas</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
              </div>
            )}
            {!customHoursEnabled && (
              <div className="p-8 rounded-3xl bg-[#F9F8F6] border border-dashed border-[#D4D0C8] text-center">
                <Clock className="w-10 h-10 mx-auto mb-3 text-[#A8A29A]" />
                <p className="text-sm text-[#635E54]">
                  {language === 'pt'
                    ? 'O horário padrão do salão será utilizado: Segunda a Sexta, 08:00 - 20:00'
                    : 'Standard salon hours will be used: Monday to Friday, 08:00 - 20:00'}
                </p>
              </div>
            )}
            {!myStaffId && (
              <div className="p-6 rounded-2xl bg-amber-50 border border-amber-200">
                <p className="text-sm text-amber-800">
                  {language === 'pt'
                    ? 'Para editar horários de colaboradores, acesse o painel Admin > Equipa e edite o horário de cada colaborador individualmente.'
                    : 'To edit staff schedules, go to Admin > Team and edit each staff member\'s schedule individually.'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
export default StaffDashboardPage;
